import { useCallback, useMemo, useState } from 'react'
import { Button, FormDialog } from '../../rapid-ui'
import { CanvasData } from '../canvas/data'

export function CanvasExportDialog({ open, onClose, document }: {
	open: boolean
	onClose: () => void
	document: string | null | undefined
}) {
	const serialized = useMemo(() => {
		if (!document) { return '' }
		try {
			return JSON.stringify(JSON.parse(document), null, 2)
		} catch {
			return document
		}
	}, [document])
	const [copied, setCopied] = useState<boolean | string>(false)

	const handleCopy = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(serialized)
			setCopied(true)
			setTimeout(() => setCopied(false), 2000)
		} catch {
			setCopied('Failed to copy')
			setTimeout(() => setCopied(false), 2000)
		}
	}, [serialized])

	return (
		<FormDialog open={open} onClose={onClose} title="Export canvas document" maxWidth="lg">
			<textarea
				value={serialized}
				readOnly
				className="w-full rounded border border-grey-200 bg-grey-50 p-2 font-mono text-[11px] text-grey-800 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
				rows={18}
				spellCheck={false}
				onFocus={(e) => e.target.select()}
			/>
			<div className="flex justify-between mt-4">
				<Button type="subtle" size="normal" onClick={handleCopy}>
					{copied === true
						? 'Copied!'
						: typeof copied === 'string'
						? copied
						: 'Copy to clipboard'}
				</Button>
				<Button type="subtle" size="normal" onClick={onClose}>
					Close
				</Button>
			</div>
		</FormDialog>
	)
}

export function CanvasImportDialog({ open, onClose, onLoad }: {
	open: boolean
	onClose: () => void
	onLoad: (doc: CanvasData) => void
}) {
	const [draft, setDraft] = useState('')
	const [error, setError] = useState<string | null>(null)

	const handlePaste = useCallback(async () => {
		try {
			const text = await navigator.clipboard.readText()
			setDraft(text)
			setError(null)
		} catch {
			setError('Clipboard access denied')
		}
	}, [])

	const handleLoad = useCallback(() => {
		try {
			const parsed = CanvasData.parse(JSON.parse(draft))
			setError(null)
			onLoad(parsed)
			setDraft('')
			onClose()
		} catch (e) {
			setError(e instanceof Error ? e.message : 'Invalid JSON')
		}
	}, [draft, onLoad, onClose])

	const handleClose = useCallback(() => {
		setDraft('')
		setError(null)
		onClose()
	}, [onClose])

	return (
		<FormDialog open={open} onClose={handleClose} title="Import canvas document" maxWidth="lg">
			<textarea
				value={draft}
				onChange={(e) => {
					setDraft(e.target.value)
					setError(null)
				}}
				placeholder="Paste canvas JSON here..."
				className="w-full rounded border border-grey-200 bg-grey-50 p-2 font-mono text-[11px] text-grey-800 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
				rows={18}
				spellCheck={false}
			/>
			{error && <p className="text-[11px] text-red-500 mt-1">{error}</p>}
			<div className="flex justify-between mt-4">
				<Button type="subtle" size="normal" onClick={handlePaste}>
					Paste from clipboard
				</Button>
				<div className="flex gap-3">
					<Button type="subtle" size="normal" onClick={handleClose}>
						Cancel
					</Button>
					<Button
						type="committal"
						size="normal"
						onClick={handleLoad}
						disabled={!draft.trim()}
					>
						Load
					</Button>
				</div>
			</div>
		</FormDialog>
	)
}
